import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import {
  AppSettings,
  TradeSettings,
  ChangePasswordBody,
  ChangePinBody,
  UpdateTradeSettingsBody,
} from 'common/models';
import { authRepository } from 'common/repositories';
import { settingsRepository } from 'common/repositories/settings';
import storage from 'core/services/storage';

type State = {
  isLoading: boolean;
  appSettings: AppSettings;
  tradeSettings: TradeSettings | null;
};

type Actions = {
  setLoading: (value: boolean) => void;
  setAppSettings: (value: Partial<AppSettings>) => void;
  setTradeSettings: (value: Partial<TradeSettings>) => void;
  fetchTradeSettings: () => Promise<TradeSettings | null>;
  updateTradeSettings: (
    value: UpdateTradeSettingsBody
  ) => Promise<TradeSettings | null>;
  updatePassword: (value: ChangePasswordBody) => Promise<boolean>;
  updatePin: (value: ChangePinBody) => Promise<boolean>;
};

export type SettingsStore = State & Actions;

const defaultState: State = {
  isLoading: false,
  appSettings: {
    showSnackbarErrorMessages: true,
  },
  tradeSettings: null,
};

const useSettingsStore = create<SettingsStore>()(
  persist(
    immer((set, get) => ({
      ...defaultState,

      setLoading: (value) =>
        set((state) => {
          state.isLoading = value;
        }),

      setTradeSettings: (value) =>
        set((state) => {
          state.tradeSettings = {
            ...(state.tradeSettings ?? {
              maxOpenPositions: null,
              maxOpenPositionsPerDay: null,
              sessionTradeLimit: null,
            }),
            ...value,
          };
        }),

      setAppSettings: (value) =>
        set((state) => {
          state.appSettings = {
            ...state.appSettings,
            ...value,
          };
        }),

      fetchTradeSettings: async () => {
        const { setTradeSettings, setLoading } = get();

        setLoading(true);

        const response = await settingsRepository.getTradeSettings();

        setLoading(false);

        const data = response.data;

        if (!data) {
          return null;
        }

        setTradeSettings(data);

        return data;
      },

      updateTradeSettings: async (value) => {
        const { setTradeSettings, setLoading } = get();

        setLoading(true);

        const response = await settingsRepository.updateTradeSettings(value);

        setLoading(false);

        const data = response.data;

        if (!data) {
          return null;
        }

        setTradeSettings(data);

        return data;
      },

      updatePassword: async (value) => {
        const response = await authRepository.changePassword(value);

        if (response.error) {
          return false;
        }

        return true;
      },

      updatePin: async (value) => {
        const response = await authRepository.changePin(value);

        if (response.error) {
          return false;
        }

        return true;
      },
    })),
    {
      name: storage.APP_SETTINGS_STORE_KEY,
      partialize: (state) => state.appSettings,
      merge: (persistedState, currentState) => ({
        ...currentState,
        appSettings: persistedState as AppSettings,
      }),
    }
  )
);

export const useFetchTradeSettings = () => {
  const data = useSettingsStore((state) => state.tradeSettings);
  const isLoading = useSettingsStore((state) => state.isLoading);

  useEffect(() => {
    useSettingsStore.getState().fetchTradeSettings();
  }, []);

  return { data, isLoading };
};

export default useSettingsStore;
